import React, { useState, useCallback } from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Modal,
  Button,
  TextField,
  Autocomplete,
  Stack
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomAxios from "../utils/CustomAxios";
import { countries as countries_json } from "../countries";
import { useDispatch, useSelector } from 'react-redux';
import {
  updateSnackBarMessage,
  updateSnackBarOpen,
  updateSnackBarSeverity,
} from "../redux/snackbar";

// Styled node component - clickable with hover effects
const StyledNode = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'levelColor' && prop !== 'hasChildren',
})`
  padding: 10px;
  border-radius: 8px;
  display: inline-block;
  border: 2px solid ${props => props.levelColor ? `${props.levelColor}80` : '#FFD700'};
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1), 0 0 8px ${props => props.levelColor ? `${props.levelColor}30` : '#FFD700'};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
  min-height: 120px;
  text-align: center;
  font-weight: bold;
  color: #333;
  opacity: 1;

  ${props => props.hasChildren && `
    &:hover {
      box-shadow: 0 4px 8px rgba(0,0,0,0.2), 0 0 12px ${props.levelColor ? `${props.levelColor}50` : '#FFD700'};
      transform: translateY(-2px);
      border-color: ${props.levelColor ? `${props.levelColor}aa` : '#FFD700'};
    }

    &:active {
      transform: translateY(0);
    }
  `}
`;

const StyledEmptyNode = styled.div`
  padding: 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 150px;
  min-height: 120px;
  border: 2px dashed #cbd5e1;
  background: #f8fafc;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #94a3b8;
    background: #f1f5f9;
  }
`;

const SimpleOrgChart = ({ teamTree = [], treeBoard = [], onNextTree, onBackward, name, onFetchChildren, onMetricsChange, isLoading = false, isExpandedView = false, preExpandResetKey = null, preExpandResetFlag = false }) => {
  const [visibleList, setVisibleList] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(new Set()); // Track which nodes are loading
  const [childrenData, setChildrenData] = useState({}); // Store fetched children data
  const [userTree, setUserTree] = useState({}); // Store the complete user tree data
  const autoPreExpandDoneRef = React.useRef(false);
  const prevRootKidsCountRef = React.useRef(-1);
  const prevPreExpandResetKeyRef = React.useRef(preExpandResetKey);
  const dispatch = useDispatch();

  // Get user role from Redux state
  const userRole = useSelector(state => state.user.role);

  // Create user modal states
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    country: 'India',
    dialcode: '+91',
    introducedBy: '',
    referredBy: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Function to fetch countries data
  const fetchCountries = async () => {
    try {
      const response = { "data": countries_json };
      return response.data.map((country) => ({
        value: country.cca2,
        label: country.name.common,
        dialCode: country.idd?.root + (country.idd?.suffixes?.[0] || ""),
        flag: country.flags?.png,
        numberLength: country.cca2 === "IN" ? 10 : 15,
      }));
    } catch (error) {
      console.error("Error fetching countries:", error);
      return [];
    }
  };

  // Load countries on component mount
  React.useEffect(() => {
    const getCountries = async () => {
      const countryOptions = await fetchCountries();
      setCountries(countryOptions);
      // Set India as default selected country
      const indiaOption = countryOptions.find(country => country.value === 'IN');
      if (indiaOption) {
        setSelectedCountry(indiaOption);
      }
    };
    getCountries();
  }, []);

  const getTeamBoardUserId = useCallback((member) => {
    if (member?.account?.user) {
      return member.account.user;
    }
    if (member?.user?.id) {
      return member.user.id;
    }
    // `dashboard-details/` returns flattened ORM values where:
    // - `id` is AccountDetails.id
    // - `user__id` is CustomUser.id (what `team_board/` expects as `user_id`)
    if (member?.user__id) {
      return member.user__id;
    }
    return member?.id || null;
  }, []);

  // Function to fetch children dynamically for a single user
  const fetchChildrenForUser = useCallback(async (userId) => {
    if (!userId) {
      return [];
    }

    if (childrenData[userId]) {
      // Already have the data
      return childrenData[userId];
    }

    if (loadingChildren.has(userId)) {
      // Already loading
      return null;
    }

    setLoadingChildren(prev => new Set(prev).add(userId));

    try {
      // Make direct API call with single user_id
      const response = await CustomAxios.get(`team_board/`, {
        params: {
          user_id: userId,  // Send single user_id instead of user_ids
          level: 0,
          max_depth: 2,
          page: 1,
          page_size: 50
        }
      });

      if (response.data.success) {
        const children = response.data.data.children || [];

        // Store in childrenData for the specific user
        setChildrenData(prev => ({
          ...prev,
          [userId]: children
        }));

        // Also store in userTree for the complete tree structure
        setUserTree(prev => ({
          ...prev,
          [userId]: {
            children: children,
            fetched: true,
            timestamp: new Date().toISOString()
          }
        }));

        // console.log("Fetched children for user:", userId, children);
        return children;
      }
    } catch (error) {
      console.error('Error fetching children for user:', userId, error);
    } finally {
      setLoadingChildren(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }

    return [];
  }, [childrenData, loadingChildren]);


  const getLevelColor = (level) => {
    const colors = [
      '#2196f3', // Bright Blue - Root Level
      '#9c27b0', // Bright Purple - Level 1
      '#4caf50', // Bright Green - Level 2
      '#ff9800', // Bright Orange - Level 3
      '#f44336', // Bright Red - Level 4
      '#00bcd4', // Bright Cyan - Level 5
      '#795548', // Brown - Level 6
      '#e91e63', // Bright Pink - Level 7
      '#3f51b5', // Indigo - Level 8
      '#8bc34a'  // Light Green - Level 9
    ];
    return colors[level % colors.length];
  };

  // Handle node click for expand/collapse with dynamic loading
  const handleNodeClick = async (member, lead = null) => {
    // console.log("Clicked member:", member, "lead:", lead);
    const memberId = member.id;
    const teamBoardUserId = getTeamBoardUserId(member);

    // Check if member has basic user data (first_name, last_name, or mobileno)
    const hasMemberData = member.first_name || member.last_name || getMemberMobile(member);

    if (!hasMemberData) {
      // Check if current user is admin before allowing user creation
      if (userRole === 'admin') {
        // No member data and user is admin, open create user modal
        setSelectedMember(member);

        // Auto-fill introducer and referrer fields with the lead member's referral code
        // Use lead data if available (the parent member), otherwise fallback to member
        const referralSource = lead || member;
        setFormData(prev => ({
          ...prev,
          introducedBy: referralSource.referral_code || '',
          referredBy: referralSource.referral_code || ''
        }));

        setCreateUserModalOpen(true);
      } else {
        // User is not admin, show message or do nothing
        alert('Only administrators can create new users.');
      }
      return;
    }

    if (visibleList.includes(memberId)) {
      // Already expanded, just collapse
      setVisibleList(visibleList.filter(item => item !== memberId));
      return;
    }

    // Check if we already have children data or if we need to fetch it
    const existingChildren = member.child || member.children || [];
    const hasExistingChildren = existingChildren.length > 0;

    if (!hasExistingChildren && !childrenData[teamBoardUserId]) {
      // Fetch children for only the clicked member
      await fetchChildrenForUser(teamBoardUserId);
    }

    // Now expand the node
    setVisibleList([...visibleList, memberId]);
  };

  // Handle country change
  const handleCountryChange = (event, newValue) => {
    setSelectedCountry(newValue);
    setFormData(prev => ({
      ...prev,
      country: newValue?.label || 'India',
      dialcode: newValue?.dialCode || '+91'
    }));
  };

  // Handle create user form submission
  const handleCreateUser = async () => {
    // Validate form data
    const errors = {};

    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if ((formData.mobile.length !== 10&&selectedCountry?.value === "IN") || !/^\d+$/.test(formData.mobile)) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    } 

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.introducedBy.trim()) {
      errors.introducedBy = 'Introduced by is required';
    }

    if (!formData.referredBy.trim()) {
      errors.referredBy = 'Referred by is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormErrors({});

    try {
      // Prepare the payload for creating user
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        mobile: formData.mobile.trim(),
        country: formData.country,
        introduced_by: formData.introducedBy.trim(),
        dialcode: selectedCountry?.dialCode || '+91',
        referred_by: formData.referredBy.trim(),
        position_id: selectedMember?.id || null, // Assuming selectedMember has position info
      };

      // Make API call to create user and subscribe to plan
      // This API will create user, subscribe to first plan, and distribute commissions
      const response = await CustomAxios.post('/admin/create_user_with_plan/', payload);

      if (response.data.success) {
        // Success - close modal and reset
        setCreateUserModalOpen(false);
        setSelectedMember(null);
        setFormData({
          firstName: '',
          lastName: '',
          mobile: '',
          country: selectedCountry?.label || 'India',
          dialcode: selectedCountry?.dialCode || '+91',
          introducedBy: '',
          referredBy: ''
        });

        // Update the tree data to include the new user based on referral relationship
        if (response.data.user) {
          // Clear cached data and trigger re-render to show updated tree
          // This will force the component to re-fetch data from the server
          setChildrenData({});
          setUserTree({});
          setVisibleList([]);

          // Trigger a re-render by updating state
          setCreateUserModalOpen(false);
        }

        // Show success message
        alert('User created successfully!');

        // Optional: Refresh the tree data to ensure consistency
        // You might want to call an API to refresh tree data here if needed
      } else {
        setFormErrors({ general: response.data.message || 'Failed to create user' });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setFormErrors({ general: error.response?.data?.message || 'An error occurred while creating the user' });
    } finally {
      setSubmitting(false);
    }
  };

  // Get full member name from API response
  const getFullMemberName = (member) => {
    
    if (member?.first_name && member?.last_name) {
      return `${member?.first_name} ${member?.last_name}`;
    }
    else if (member?.user__first_name && member?.user__last_name) {
      return `${member?.user__first_name} ${member?.user__last_name}`;
    }

    return member?.first_name || member?.user__first_name || '';
  };

  // Get member name with shortening
  const getMemberName = (member) => { 
    const fullName = getFullMemberName(member);
    if (fullName?.length > 12) {
      return fullName.substring(0, 12) + '...';
    }
    return fullName;
  };

  // Get member mobile number from CustomUser table
  const getMemberMobile = (member) => {
    // Prioritize CustomUser mobile number
    if (member?.user?.mobileno) {
      return member.user.mobileno;
    } else if (member?.mobileno) {
      // This could be from CustomUser or AccountDetails
      return member.mobileno;
    } else if (member?.account?.mobileno) {
      return member.account.mobileno;
    }
    else if (member?.user__mobileno) {
      return member.user__mobileno;
    }
    return '';
  };

  // Get member spending
  const getMemberCumulativeSpend = (member) => {
    if (member?.account?.cumulative_slab_spend) {
      return member.account.cumulative_slab_spend;
    } else if (member?.user?.total_spend) {
      return member.user.total_spend;
    } else if (member?.total_spend) {
      return member.total_spend;
    }
    return 'N/A';
  };

  // Handle referral code copy
  const handleCopy = async (referralCode) => {
    const baseUrl = process.env.REACT_APP_URL || window.location.origin;
    const referralLink = `${baseUrl}signup/?code=${referralCode}`;
    // console.log("?????????????????/referralLink",referralLink)

    try {
      await navigator.clipboard.writeText(referralLink);
      dispatch(updateSnackBarMessage("Referral link copied to clipboard!"));
      dispatch(updateSnackBarSeverity("success"));
      dispatch(updateSnackBarOpen(true));
    } catch (err) {
      console.error('Failed to copy: ', err);
      dispatch(updateSnackBarMessage("Failed to copy referral link."));
      dispatch(updateSnackBarSeverity("error"));
      dispatch(updateSnackBarOpen(true));
    }
  };

  // Get tooltip content
  const getTooltipContent = (member) => {
    const children = member?.child || member?.children || [];
    const name = getFullMemberName(member);
    const mobile = getMemberMobile(member);
    const spend = getMemberCumulativeSpend(member);

    let tooltip = `Name: ${name}\nMobile: ${mobile}\nSpent: ₹${spend}`;

    if (children.length > 0) {
      tooltip += `\n\nTeam Members: ${children.length}`;
    }

    return tooltip;
  };

  const getSlotCapacity = (level) => (level === 0 ? 1 : 3);

  const getRenderableChildren = (items, level, parentMember) => {
    if (userRole !== 'admin') {
      return items;
    }

    const realItems = Array.isArray(items) ? items : [];
    const capacity = getSlotCapacity(level);
    const visibleItems = realItems.slice(0, capacity);
    const placeholders = Array.from(
      { length: Math.max(capacity - visibleItems.length, 0) },
      (_, index) => ({
        __empty: true,
        id: `empty-${parentMember?.id || 'root'}-${level}-${index}`,
        lead: parentMember,
      })
    );

    return [...visibleItems, ...placeholders];
  };

  const collectExpandableNodeIds = (rootNode) => {
    const ids = [];

    const getChildrenForNode = (node, isRootNode = false) => {
      if (!node || node.__empty) {
        return [];
      }

      if (isRootNode) {
        return rootNode?.members || rootNode?.children || [];
      }

      const teamBoardUserId = getTeamBoardUserId(node);
      const fetchedChildren = childrenData[teamBoardUserId] || [];
      const existingChildren = node?.child || node?.children || [];

      return fetchedChildren.length > 0 ? fetchedChildren : existingChildren;
    };

    const walk = (node, isRootNode = false) => {
      if (!node || node.__empty) {
        return;
      }

      const children = getChildrenForNode(node, isRootNode);
      const hasPotentialChildren = children.length > 0 || Boolean(node?.direct_members_count) || Boolean(node?.has_more_children);

      if (hasPotentialChildren && node.id) {
        ids.push(node.id);
      }

      children.forEach((child) => walk(child, false));
    };

    if (rootNode?.member) {
      walk(rootNode.member, true);
    }

    return ids;
  };

  // Recursive function to render tree nodes
  const renderTreeNode = (member, level = 0, lead = null) => {
    if (!member) {
      return null;
    }

    if (member.__empty) {
      return (
        <TreeNode
          key={member.id}
          label={
            <StyledEmptyNode onClick={() => handleNodeClick(member, member.lead || lead)}>
              Available Slot
            </StyledEmptyNode>
          }
        />
      );
    }

    const memberId = member.id;
    const teamBoardUserId = getTeamBoardUserId(member);
    const isLoading = loadingChildren.has(teamBoardUserId);
    const fetchedChildren = childrenData[teamBoardUserId] || [];
    const existingChildren = member?.child || member?.children || [];
    const children = fetchedChildren.length > 0 ? fetchedChildren : existingChildren;
    const directChildrenCount =
      fetchedChildren.length > 0
        ? fetchedChildren.length
        : member?.direct_members_count != null && member?.direct_members_count !== ''
          ? Number(member.direct_members_count)
          : existingChildren.length;
    const hasChildren = children.length > 0 || directChildrenCount > 0 || Boolean(member?.has_more_children);
    const isExpanded = visibleList.includes(memberId);
    const renderableChildren = getRenderableChildren(children, level, member);
    const hasRenderableChildren = renderableChildren.length > 0;
    const shouldRenderBranch = isExpanded && hasRenderableChildren;
    const nodeLabel = (
      <StyledNode
        levelColor={getLevelColor(level)}
        hasChildren={hasChildren}
        onClick={() => handleNodeClick(member, lead)}
        title={getTooltipContent(member)}
      >
        {getMemberName(member)}
        <br />

        {member.referral_code && (
          <>
            <small
              style={{ fontSize: '0.7em', color: '#666', fontWeight: "400", cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                handleCopy(member.referral_code);
              }}
            >
              {member.referral_code}
              <ContentCopyIcon sx={{ fontSize: "0.8em", fontWeight: 700, marginLeft: "2px" }} />
            </small>
            <br />
          </>
        )}

        <small style={{ fontSize: '0.7em', color: '#888', fontWeight: "400" }}>
          Mobile No: {getMemberMobile(member)}
        </small>
        <br />

        {/* <small style={{ fontSize: '0.7em', color: '#888', fontWeight: "400" }}>
          Spent: ₹{getMemberCumulativeSpend(member)}
        </small> */}

        {(hasChildren || isLoading) && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Chip
                label={`${directChildrenCount} Members`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </StyledNode>
    );

    if (!shouldRenderBranch) {
      return <TreeNode key={member.id} label={nodeLabel} />;
    }

    // console.log("Rendering member:", memberId, "hasChildren:", hasChildren, "isExpanded:", isExpanded, "children:", children)
    return (
      <TreeNode key={member.id} label={nodeLabel}>
        {renderableChildren.map((child) => renderTreeNode(child, level + 1, member))}
      </TreeNode>
    );
  };

  // Build the tree structure
  const buildTreeStructure = () => {
    if (treeBoard?.length) {
      return treeBoard[treeBoard.length - 1];
    } else if (teamTree?.length) {
      return teamTree[0];
    }
    return null;
  };

  const rootMember = buildTreeStructure();
  let member=rootMember?.member;

  // Explicit account-switch reset trigger (epoch/key from parent).
  // When key changes, rerun lvl2 auto pre-expand deterministically.
  React.useEffect(() => {
    if (prevPreExpandResetKeyRef.current === preExpandResetKey) return;
    prevPreExpandResetKeyRef.current = preExpandResetKey;
    autoPreExpandDoneRef.current = false;
    prevRootKidsCountRef.current = -1;
    setVisibleList([]);
  }, [preExpandResetKey]);

  // Explicit boolean toggle (e.g. account switch) to force deterministic reset.
  React.useEffect(() => {
    autoPreExpandDoneRef.current = false;
    prevRootKidsCountRef.current = -1;
    setVisibleList([]);
    setChildrenData({});
    setLoadingChildren(new Set());
  }, [preExpandResetFlag]);

  // After login, tree often mounts with 0 lvl1 nodes then updates; reset auto-expand so lvl2 can show.
  React.useEffect(() => {
    const n = (rootMember?.members || rootMember?.children || []).length;
    if (n > 0 && prevRootKidsCountRef.current === 0) {
      autoPreExpandDoneRef.current = false;
    }
    prevRootKidsCountRef.current = n;
  }, [rootMember]);

  React.useEffect(() => {
    if (!onMetricsChange) {
      return;
    }

    if (!rootMember?.member) {
      onMetricsChange({ totalMembers: 0, maxDepth: 0 });
      return;
    }

    const getNodeChildren = (node) => {
      if (!node || node.__empty) {
        return [];
      }

      const teamBoardUserId = getTeamBoardUserId(node);
      const fetchedChildren = childrenData[teamBoardUserId] || [];
      const existingChildren = node?.child || node?.children || [];

      return fetchedChildren.length > 0 ? fetchedChildren : existingChildren;
    };

    const walkTree = (node, currentLevel) => {
      if (!node || node.__empty) {
        return { totalMembers: 0, maxDepth: 0 };
      }

      const children = getNodeChildren(node);
      let totalMembers = 1;
      let maxDepth = Math.min(currentLevel, 9);

      children.forEach((child) => {
        const childMetrics = walkTree(child, currentLevel + 1);
        totalMembers += childMetrics.totalMembers;
        maxDepth = Math.max(maxDepth, childMetrics.maxDepth);
      });

      return {
        totalMembers,
        maxDepth: Math.min(maxDepth, 9),
      };
    };

    const rootChildren = rootMember?.members || rootMember?.children || [];
    const rootMetrics = walkTree(rootMember.member, 1);

    rootChildren.forEach((child) => {
      const childMetrics = walkTree(child, 2);
      rootMetrics.totalMembers += childMetrics.totalMembers;
      rootMetrics.maxDepth = Math.max(rootMetrics.maxDepth, childMetrics.maxDepth);
    });

    onMetricsChange({
      totalMembers: Math.max(rootMetrics.totalMembers - 1, 0),
      maxDepth: Math.max(Math.min(rootMetrics.maxDepth, 9) - 1, 0),
    });
  }, [childrenData, onMetricsChange, rootMember]);

  React.useEffect(() => {
    if (!rootMember?.member) return;
    if (!isExpandedView) return;
    setVisibleList(collectExpandableNodeIds(rootMember));
  // Important: do NOT reset visibleList when childrenData changes.
  // childrenData changes during lazy-loading (fetching lvl2 nodes),
  // and resetting would collapse the auto-expanded lvl1 -> lvl2 view.
  }, [isExpandedView, rootMember]);

  // When "Expand All" is enabled, recompute visible nodes as children load.
  // In normal mode we avoid resetting visibleList on childrenData changes.
  React.useEffect(() => {
    if (!rootMember?.member) return;
    if (!isExpandedView) return;
    setVisibleList(collectExpandableNodeIds(rootMember));
  }, [childrenData, isExpandedView, rootMember]);

  // Dashboard default behavior:
  // - If a node has lvl2 children, pre-expand lvl1 and show lvl2 by default.
  // - If no lvl2 exists, we keep only the node visible (no placeholders expanded).
  React.useEffect(() => {
    // Only run auto pre-expand once per new tree root.
    if (!rootMember?.member) return;
    if (isExpandedView) return; // "Expand All" controls visibility.
    if (autoPreExpandDoneRef.current) return;

    const rootChildren = rootMember?.members || rootMember?.children || [];
    const lvl1Nodes = Array.isArray(rootChildren) ? rootChildren : [];

    // If there are no lvl1 nodes yet, wait for data (do not mark done — avoids stuck collapsed after login).
    if (lvl1Nodes.length === 0) {
      setVisibleList([]);
      return;
    }

    const run = async () => {
      const expandedLvl1Ids = [];

      for (const lvl1Node of lvl1Nodes) {
        const teamBoardUserId = getTeamBoardUserId(lvl1Node);
        if (!teamBoardUserId) continue;

        // Level 2 already embedded on the node (e.g. dashboard-details with lvl2_members) —
        // expand without calling team_board/ so nodes don't show the in-node loading spinner.
        const embedded =
          lvl1Node?.child ||
          lvl1Node?.children ||
          [];
        if (Array.isArray(embedded) && embedded.length > 0) {
          expandedLvl1Ids.push(lvl1Node.id);
          continue;
        }

        const fetchedChildren = await fetchChildrenForUser(teamBoardUserId);
        if (Array.isArray(fetchedChildren) && fetchedChildren.length > 0) {
          // Tree expand condition uses member.id as node id.
          expandedLvl1Ids.push(lvl1Node.id);
        }
      }

      setVisibleList(expandedLvl1Ids);
      autoPreExpandDoneRef.current = true;
    };

    run().catch(() => {
      // If prefetch fails for any reason, keep the safe default (no forced expansion).
      autoPreExpandDoneRef.current = true;
      setVisibleList([]);
    });
  }, [isExpandedView, rootMember, fetchChildrenForUser, getTeamBoardUserId, preExpandResetFlag]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 240,
          gap: 2,
          color: '#64748b',
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2">Loading team structure...</Typography>
      </Box>
    );
  }

  if (!rootMember) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        fontSize: '18px',
        color: '#666'
      }}>
        No Team Data Available
      </Box>
    );
  }

    const rootChildren = rootMember?.members || rootMember?.children || [];
    const renderableRootChildren = getRenderableChildren(rootChildren, 0, member);
    const rootDirectCount = Math.max(
      rootChildren.length,
      Number.isFinite(Number(rootMember?.direct_members_count))
        ? Number(rootMember.direct_members_count)
        : 0
    );
    const rootHasChildren = rootChildren.length > 0;
    const rootLabel = (
      <StyledNode
        levelColor={getLevelColor(0)}
        hasChildren={rootHasChildren}
        title={getTooltipContent(member)}
        onClick={() => handleNodeClick(member, null)}
      >
        {getMemberName(member)}
        <br />

        {member?.referral_code && (
          <>
            <small
              style={{ fontSize: '0.7em', color: '#666', fontWeight: "400", cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                handleCopy(member?.referral_code);
              }}
            >
              {member.referral_code}
              <ContentCopyIcon sx={{ fontSize: "0.8em", fontWeight: 700, marginLeft: "2px" }} />
            </small>
            <br />
          </>
        )}

        <small style={{ fontSize: '0.7em', color: '#888', fontWeight: "400" }}>
          Mobile: {getMemberMobile(member)}
        </small>
        <br />

        <small style={{ fontSize: '0.7em', color: '#888', fontWeight: "400" }}>
          Spent: ₹{getMemberCumulativeSpend(member)}
        </small>

        {rootHasChildren && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={`${rootDirectCount} Direct Members`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </StyledNode>
    );
    const adminRootPlaceholders = !rootHasChildren
      ? renderableRootChildren.filter((child) => child?.__empty)
      : [];

    // console.log("????????children_update",children_update)
    return (
    <Box sx={{
      width: '100%',
      height: '100%',
      overflow: 'auto',
      px: { xs: 1, sm: 2 },
      py: { xs: 1, sm: 2 }
    }}>
        {rootHasChildren ? (
        <Tree
          lineWidth={'2px'}
          lineColor={'#90a4ae'}
          lineBorderRadius={'8px'}
          label={rootLabel}
        >
          {renderableRootChildren.map((child) => renderTreeNode(child, 1, member))}
        </Tree>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {rootLabel}
            {adminRootPlaceholders.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {adminRootPlaceholders.map((slot) => (
                  <StyledEmptyNode key={slot.id} onClick={() => handleNodeClick(slot, member)}>
                    Available Slot
                  </StyledEmptyNode>
                ))}
              </Box>
            )}
          </Box>
        )}

      {/* Instructions */}
      {/* <Box sx={{
        mt: 2,
        p: 2,
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          How to use:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click on any user node to expand/collapse their team members. Click on referral codes to copy the referral link.
        </Typography>
      </Box> */}

      {/* Create User Modal */}
      <Modal
        open={createUserModalOpen}
        onClose={() => {
          setCreateUserModalOpen(false);
          setSelectedMember(null);
          // Reset to India as default
          const indiaOption = countries.find(country => country.value === 'IN');
          setSelectedCountry(indiaOption || null);
          setFormData({
            firstName: '',
            lastName: '',
            mobile: '',
            country: 'India',
            dialcode: '+91',
            introducedBy: '',
            referredBy: ''
          });
          setFormErrors({});
        }}
        aria-labelledby="create-user-modal-title"
        aria-describedby="create-user-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          minWidth: 400,
          maxWidth: 500,
        }}>
          <Typography id="create-user-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
            Create New User
          </Typography>

          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}

          <Stack spacing={2}>

            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
            />

            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
            />

            <Box>
              <Typography sx={{ marginBottom: "12px", fontWeight: 400, fontSize: "14px", color: "grey.900", textAlign: "left" }}>
                Country <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                sx={{ padding: "0px", height: "48px" }}
                style={{ width: "100%", height: "48px" }}
                options={countries}
                getOptionLabel={(option) => option.label}
                onChange={handleCountryChange}
                value={selectedCountry || null}
                isOptionEqualToValue={(option, value) =>
                  value ? option?.value === value?.value : false
                }
                renderOption={(props, option) => (
                  <Box
                    {...props}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <img
                      src={option.flag}
                      alt={option.label}
                      style={{
                        width: "20px",
                        height: "15px",
                        marginRight: "8px",
                        borderRadius: "2px"
                      }}
                    />
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "48px",
                        borderRadius: "5px",
                        border: "1px solid",
                        borderColor: "grey.400"
                      }
                    }}
                  />
                )}
              />
            </Box>
            <TextField
              fullWidth
              label="Mobile Number"
              value={formData.mobile}
              onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
              error={!!formErrors.mobile}
              helperText={formErrors.mobile}
            />

            <TextField
              fullWidth
              label="Introduced By"
              value={formData.introducedBy}
              onChange={(e) => setFormData(prev => ({ ...prev, introducedBy: e.target.value }))}
              error={!!formErrors.introducedBy}
              helperText={formErrors.introducedBy}
            />

            <TextField
              fullWidth
              label="Referred By"
              value={formData.referredBy}
              onChange={(e) => setFormData(prev => ({ ...prev, referredBy: e.target.value }))}
              error={!!formErrors.referredBy}
              helperText={formErrors.referredBy}
            />
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCreateUserModalOpen(false);
                setSelectedMember(null);
                // Reset to India as default
                const indiaOption = countries.find(country => country.value === 'IN');
                setSelectedCountry(indiaOption || null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  mobile: '',
                  country: 'India',
                  dialcode: '+91',
                  introducedBy: '',
                  referredBy: ''
                });
                setFormErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create User'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SimpleOrgChart;
